export interface IImageDimensions {
  width: number
  height: number
}

/**
 * Reads an image's intrinsic pixel size from the local `File`, before it is uploaded.
 *
 * Nothing persists pixel dimensions — `IUploadedFile.size` is bytes, and neither the
 * file payload nor the backend's record carries width/height — so the composer is the
 * one moment the real shape is knowable. Without it the loading skeleton can only be a
 * square, and every non-square image snaps to its true size once decoded.
 *
 * Both numbers, not just the ratio: an `<img>` left at `width: auto` scales *down* to
 * the size caps but never *up* to them, so the skeleton needs the pixel width too or
 * it reserves a full-cap box that any smaller image then shrinks out of.
 */
export const readImageDimensions = (
  file: File,
): Promise<IImageDimensions | undefined> =>
  new Promise((resolve) => {
    if (!file.type.startsWith('image/')) {
      resolve(undefined)
      return
    }

    const objectUrl = URL.createObjectURL(file)
    const image = new Image()

    const settle = (dimensions?: IImageDimensions) => {
      URL.revokeObjectURL(objectUrl)
      resolve(dimensions)
    }

    image.onload = () => {
      const { naturalWidth, naturalHeight } = image
      // SVGs without intrinsic dimensions report 0; the square default is right for them.
      settle(
        naturalWidth > 0 && naturalHeight > 0
          ? { width: naturalWidth, height: naturalHeight }
          : undefined,
      )
    }
    image.onerror = () => settle(undefined)
    image.src = objectUrl
  })
